import { Link } from "react-router-dom";

import Constellation from "@/components/landing/dcv2/Constellation";
import DcPage, { useDcTheme } from "@/components/landing/dcv2/DcPage";
import { SITE } from "@/config/site";

/**
 * Meet Our CEO — /about/ceo
 *
 * THE IDEA: he is the node the network resolves to.
 *
 * The product is HiveMind — collective intelligence across a network — so this
 * page puts the person inside that network rather than beside a description of
 * it. The canvas's own particle field runs live behind the whole stage and
 * answers the pointer, an orbit carries a lit node around his portrait, and the
 * headline runs under the portrait's glow instead of stopping politely beside
 * it. He is the one fixed point in a field that is otherwise always moving.
 *
 * WHY THIS, AFTER THREE OTHERS. The previous three builds — an instrument
 * panel, a signed statement and a ruled ledger — were all austere typographic
 * layouts, and all three were rejected. They were spare pages on a canvas that
 * is not spare: the homepage carries this same particle field, a starfield,
 * orbit rings, a perspective grid and a full dashboard. The fault was never the
 * composition, it was that the page used almost none of what the design system
 * already ships. This build uses it.
 *
 * Every effect here is the canvas's own, not a new world: `Constellation` is
 * the homepage hero's component, `dcv2-spinSlow` and `dcv2-pulseDot` are
 * keyframes already in the shared stylesheet, the blooms are `data-plx`, the
 * dark close band is the same one /about closes on, and the palette is the
 * eleven custom properties. The richness is composition, depth and motion —
 * not a foreign typeface or a private palette. That constraint is the
 * correction from the instrument-panel build and it still holds.
 *
 * Superseded and NOT to be reintroduced, now six deep: the signed letter over a
 * grayscale silhouette; the full-height environmental portrait; the
 * "Meet the CEO.dc.html" mounted print; the anodized instrument panel; the
 * signed-statement composition; and the ruled ledger.
 *
 * The photograph rule has survived all six and still holds: nothing is done to
 * it. No grayscale, no duotone, no vignette, no blend mode. It is lit from
 * outside — the glow and the orbit are around the print, never on it.
 */

/* ─────────────────────── Canvas tokens ─────────────────────── */

const SANS = "'Manrope', sans-serif";
const MONO = "'Manrope', sans-serif";
const HAIRLINE = "1px solid rgba(var(--ink-rgb),.13)";

const CEO_NAME = "Jacob Christian";
const CEO_TITLE = "CEO & Founder, AI2Aim";

/**
 * Confirmed by the user as his own copy and accurate — the figures were
 * corrected on 2026-09-18 from 15+ / 2 / "PH & CA". PRODUCT.md bars *invented*
 * claims; these are supplied ones, so they ship as written.
 *
 * The lead statement was corrected in the same pass: it read "after fifteen
 * years watching capable teams…", which contradicted this record the moment the
 * figure moved to 20+. The two numbers describe the same span and now agree, so
 * keep them in step — changing one without the other puts a visible
 * inconsistency in front of the buyer this page is written for.
 */
const RECORD = [
  { value: "20+", label: "Years in operations" },
  { value: "200+", label: "Workflows deployed" },
  { value: "3", label: "Offices · PH, CA, IN" },
];

const STATEMENT_LEAD =
  "Jacob started AI2Aim after twenty years watching capable teams lose their weeks to work software should have handled. The company exists to close that gap: automation that fits how a business already runs, not a platform it has to bend around.";

const STATEMENT_CLOSE =
  "He still sits in on discovery calls. If you want a direct answer about whether this would work inside your organization, including the parts we haven't solved yet, he's the person to ask.";

/** A decorative cyan bloom, parallaxed by the shared runtime. */
/**
 * The portrait, held in orbit.
 *
 * Four layers, outside-in: a soft cyan halo, a hairline orbit ring carrying one
 * lit node, the elevated print, and the canvas's saturated left edge. The ring
 * rotates on `dcv2-spinSlow` and the node pulses on `dcv2-pulseDot`, both
 * already in the shared stylesheet, so `prefers-reduced-motion` stops them
 * through the canvas's existing kill switch without this route handling it.
 *
 * `data-rv` sits on the outer wrapper and nowhere inside: the reveal runtime
 * writes an inline transform on whatever it reveals, which would overwrite the
 * ring's rotation the moment the group landed.
 */
function OrbitPortrait() {
  return (
    <div
      data-rv=""
      className="dc-ceo-portrait"
      style={{ position: "relative", justifySelf: "center", padding: "26px 0" }}
    >
      {/* The halo. Sits behind everything and is the reason the headline can
          run underneath without the type falling into a hard edge. */}
      <div
        aria-hidden="true"
        className="dc-ceo-halo"
        style={{
          position: "absolute",
          inset: "-16%",
          borderRadius: "50%",
          pointerEvents: "none",
          background:
            "radial-gradient(closest-side, rgba(56,229,255,.20), rgba(56,229,255,.05) 62%, rgba(56,229,255,0))",
        }}
      />

      {/* The orbit, with one lit node riding it. */}
      <div
        aria-hidden="true"
        className="dc-ceo-orbit"
        style={{
          position: "absolute",
          inset: "-7%",
          borderRadius: "50%",
          border: "1px solid rgba(56,229,255,.24)",
          pointerEvents: "none",
          animation: "dcv2-spinSlow 34s linear infinite",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: -4,
            left: "50%",
            width: 8,
            height: 8,
            marginLeft: -4,
            borderRadius: "50%",
            background: "var(--acc2)",
            boxShadow: "0 0 12px 2px rgba(56,229,255,.55)",
            animation: "dcv2-pulseDot 2.6s ease-in-out infinite",
          }}
        />
      </div>

      {/* A second, counter-rotating orbit at a different radius, so the field
          around him reads as depth rather than as one drawn circle. */}
      <div
        aria-hidden="true"
        className="dc-ceo-orbit-outer"
        style={{
          position: "absolute",
          inset: "-15%",
          borderRadius: "50%",
          border: "1px dashed rgba(var(--ink-rgb),.14)",
          pointerEvents: "none",
          animation: "dcv2-spinSlow 68s linear infinite reverse",
        }}
      />

      <figure
        className="dc-ceo-print"
        style={{
          position: "relative",
          margin: 0,
          width: "min(78vw, 396px)",
          background: "var(--paper2)",
          border: HAIRLINE,
          borderRadius: 20,
          padding: 10,
          boxShadow: "0 30px 70px rgba(9,9,7,.32)",
        }}
      >
        <div
          style={{ position: "relative", overflow: "hidden", borderRadius: 13 }}
        >
          <img
            src="/bossing.jpg"
            alt={`${CEO_NAME}, ${CEO_TITLE}`}
            width={942}
            height={1024}
            loading="eager"
            decoding="async"
            /* Lowercase through a spread: React 18 does not map the camelCase
               prop onto the attribute and warns about it, and the lowercase
               name is not in the JSX typings. */
            {...{ fetchpriority: "high" }}
            style={{
              display: "block",
              width: "100%",
              aspectRatio: "4 / 5",
              objectFit: "cover",
              /* His face sits high in the source, so the crop is pulled up. */
              objectPosition: "50% 20%",
              background: "var(--paper2)",
            }}
          />
          {/* The page's only saturated edge, as the canvas sets it. */}
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              insetBlock: 0,
              left: 0,
              width: 2,
              background: "var(--acc2)",
            }}
          />
        </div>
      </figure>
    </div>
  );
}

/**
 * The stage, as a child of <DcPage>.
 *
 * Split out because the theme lives in a context DcPage provides: a component
 * that *renders* DcPage sits outside its own provider, and `useDcTheme` throws.
 * Constellation needs the resolved theme to pick its palette, so it has to be
 * read down here.
 */
function Stage() {
  const { dark } = useDcTheme();

  return (
    <>
      {/* `id="hero"` is load-bearing, not decoration: Constellation binds its
          pointer listeners to `canvas.closest("#hero")`. Without it the field
          still drifts but stops answering the pointer, which is most of what
          makes it feel alive. The homepage's own hero is a <header>, so the two
          never collide, and only one route renders at a time. */}
      <section
        id="hero"
        aria-labelledby="ceo-name"
        className="site-gutter"
        style={{
          position: "relative",
          overflow: "clip",
          paddingTop: "clamp(64px, 8vw, 104px)",
          paddingBottom: "clamp(56px, 6vw, 88px)",
        }}
      >
        {/* The perspective grid, on the canvas's own gridFlow drift. */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            opacity: dark ? 0.5 : 0.28,
            backgroundImage:
              "linear-gradient(rgba(var(--ink-rgb),.055) 1px, transparent 1px), linear-gradient(90deg, rgba(var(--ink-rgb),.055) 1px, transparent 1px)",
            backgroundSize: "44px 44px, 44px 44px",
            maskImage:
              "radial-gradient(120% 90% at 62% 42%, #000 20%, transparent 78%)",
            WebkitMaskImage:
              "radial-gradient(120% 90% at 62% 42%, #000 20%, transparent 78%)",
            animation: "dcv2-gridFlow 14s linear infinite",
          }}
        />

        {/* The live field. Above the backdrops, below the copy. */}
        <Constellation dark={dark} />

        <div
          className="dc-ceo-stage"
          style={{
            maxWidth: 1240,
            margin: "0 auto",
            position: "relative",
            zIndex: 3,
          }}
        >
          <div style={{ position: "relative", zIndex: 2 }}>
            <div
              data-rv=""
              style={{
                fontFamily: MONO,
                fontSize: 11.5,
                letterSpacing: ".16em",
                textTransform: "uppercase",
                color: "var(--link)",
                marginBottom: 22,
              }}
            >
              <span className="dc-mark" aria-hidden="true" /> Leadership
            </div>

            {/* The name leads the page.
                It used to sit at 17px in a pill at the foot of the column,
                which read as metadata rather than as the subject — and made
                the document's h1 the smallest heading on it. It is now the
                largest thing on the page, and his statement supports it. */}
            <h1
              data-rv=""
              id="ceo-name"
              style={{
                margin: 0,
                fontFamily: SANS,
                fontWeight: 800,
                fontSize: "clamp(40px, 5.4vw, 74px)",
                lineHeight: 1.02,
                letterSpacing: "-0.04em",
                color: "var(--ink)",
                textWrap: "balance",
              }}
            >
              {CEO_NAME}
            </h1>

            {/* The title, with the lit node carried over from the old chip so
                the accent survives the change. */}
            <div
              data-rv=""
              style={{
                marginTop: 16,
                display: "inline-flex",
                alignItems: "center",
                gap: 11,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  flex: "none",
                  borderRadius: "50%",
                  background: "var(--acc2)",
                  boxShadow: "0 0 10px 2px rgba(56,229,255,.5)",
                  animation: "dcv2-pulseDot 2.6s ease-in-out infinite",
                }}
              />
              <span
                style={{
                  fontFamily: MONO,
                  fontSize: 12.5,
                  letterSpacing: ".18em",
                  textTransform: "uppercase",
                  color: "var(--link)",
                }}
              >
                {CEO_TITLE}
              </span>
            </div>

            {/* His words, still substantial but now clearly the support. */}
            <p
              data-rv=""
              style={{
                margin: "30px 0 0",
                fontFamily: SANS,
                fontWeight: 500,
                fontSize: "clamp(17px, 1.65vw, 23px)",
                lineHeight: 1.48,
                letterSpacing: "-0.014em",
                color: "rgba(var(--ink-rgb),.78)",
                maxWidth: "40ch",
              }}
            >
              {STATEMENT_LEAD}
            </p>
          </div>

          <OrbitPortrait />
        </div>

        {/* The record, riding under the stage on the same field. */}
        <div
          className="dc-ceo-chips"
          style={{
            maxWidth: 1240,
            margin: "clamp(44px, 5vw, 72px) auto 0",
            position: "relative",
            zIndex: 3,
          }}
        >
          {RECORD.map((row) => (
            <div
              key={row.label}
              data-rv=""
              className="dc-ceo-chip"
              style={{
                background: "var(--paper2)",
                border: HAIRLINE,
                borderRadius: 16,
                padding: "22px 24px",
              }}
            >
              <div
                style={{
                  fontFamily: MONO,
                  fontWeight: 500,
                  fontSize: "clamp(28px, 3.2vw, 40px)",
                  lineHeight: 1,
                  letterSpacing: "-0.02em",
                  color: "var(--acc2)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {row.value}
              </div>
              <div
                style={{
                  marginTop: 11,
                  fontFamily: MONO,
                  fontSize: 10.5,
                  letterSpacing: ".15em",
                  textTransform: "uppercase",
                  color: "rgba(var(--ink-rgb),.62)",
                }}
              >
                {row.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── The close, on the canvas's dark band ──────────────────────── */}
      <section
        aria-labelledby="ceo-close-heading"
        className="dc-seam dc-seam-foot"
        style={{
          position: "relative",
          overflow: "clip",
          background: "var(--band)",
          color: "var(--band-ink)",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: "50%",
            right: -220,
            width: 640,
            height: 640,
            marginTop: -320,
            border: "1px solid rgba(var(--band-acc-rgb),.18)",
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        />

        <div
          className="site-gutter"
          style={{
            maxWidth: 1240,
            margin: "0 auto",
            paddingTop: "clamp(72px, 8vw, 112px)",
            paddingBottom: "clamp(72px, 8vw, 112px)",
            position: "relative",
          }}
        >
          <h2
            data-rv=""
            id="ceo-close-heading"
            style={{
              margin: 0,
              fontFamily: SANS,
              fontWeight: 600,
              fontSize: "clamp(21px, 2.4vw, 32px)",
              lineHeight: 1.36,
              letterSpacing: "-0.02em",
              maxWidth: "26ch",
            }}
          >
            {STATEMENT_CLOSE}
          </h2>

          <div
            data-rv=""
            style={{
              marginTop: 36,
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <Link
              to="/contact"
              className="dc-h-btn-cyan"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                background: "rgb(var(--band-acc-rgb))",
                color: "var(--band-on-acc)",
                padding: "16px 30px",
                borderRadius: 999,
                fontWeight: 700,
                fontSize: 15.5,
                transition: "transform .2s, box-shadow .2s",
              }}
            >
              Book a demo
            </Link>
            {/* The canvas left this a `#linkedin` placeholder. The real
                destination is shared with both footers — see config/site.ts
                for why it is not a literal here. */}
            <a
              href={SITE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="dc-h-ghost-light"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                border: "1px solid rgba(var(--band-ink-rgb),.3)",
                color: "var(--band-ink)",
                padding: "16px 30px",
                borderRadius: 999,
                fontWeight: 600,
                fontSize: 15.5,
                transition: "border-color .2s, background .2s",
              }}
            >
              Connect on LinkedIn
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

export default function MeetOurCeo() {
  return (
    <DcPage>
      <Stage />
    </DcPage>
  );
}
